import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { CalendarDays, Plus, Trash2, Palmtree, Stethoscope, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function Licencias() {
  const [licencias, setLicencias] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [userId, setUserId] = useState('');
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [tipo, setTipo] = useState('anual');
  const [notas, setNotas] = useState('');

  const fetchData = async () => {
    try {
      const [licenciasData, vendorsData] = await Promise.all([api.getLicencias(showActiveOnly), api.getVendors()]);
      setLicencias(licenciasData); setUsers(vendorsData);
    } catch (error) { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActiveOnly]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!userId || !fechaInicio || !fechaFin || !tipo) { toast.error('Completa todos los campos obligatorios'); return; }
    try {
      await api.createLicencia({ user_id: userId, fecha_inicio: format(fechaInicio, 'yyyy-MM-dd'), fecha_fin: format(fechaFin, 'yyyy-MM-dd'), tipo, notas });
      toast.success('Licencia registrada'); setShowDialog(false); setUserId(''); setFechaInicio(null); setFechaFin(null); setTipo('anual'); setNotas(''); fetchData();
    } catch (error) { const detail = error.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Error al registrar licencia'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta licencia?')) return;
    try { await api.deleteLicencia(id); toast.success('Licencia eliminada'); fetchData(); } catch (error) { toast.error('Error al eliminar'); }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) { case 'anual': return <Palmtree size={14} className="text-green-600" />; case 'enfermedad': return <Stethoscope size={14} className="text-red-600" />; default: return <FileText size={14} className="text-gray-400" />; }
  };
  const getTipoBadge = (tipo) => { switch (tipo) { case 'anual': return 'badge-green'; case 'enfermedad': return 'badge-red'; default: return 'badge-blue'; } };
  const getTipoLabel = (tipo) => { switch (tipo) { case 'anual': return 'Anual'; case 'enfermedad': return 'Enfermedad'; default: return 'Otro'; } };

  return (
    <div className="space-y-6" data-testid="licencias">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Licencias</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Gestion de licencias anuales y permisos</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="segmented-control">
            <button onClick={() => setShowActiveOnly(false)} className={`segmented-button ${!showActiveOnly ? 'active' : ''}`}>Todas</button>
            <button onClick={() => setShowActiveOnly(true)} className={`segmented-button ${showActiveOnly ? 'active' : ''}`}>Activas</button>
          </div>
          <button onClick={() => setShowDialog(true)} className="btn-primary flex items-center gap-2" data-testid="add-licencia-btn"><Plus size={16} /> Nueva Licencia</button>
        </div>
      </div>

      <div className="mac-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className="table-header">Persona</th><th className="table-header">Rol</th><th className="table-header">Tipo</th><th className="table-header">Fecha Inicio</th><th className="table-header">Fecha Fin</th><th className="table-header">Notas</th><th className="table-header w-16">Acciones</th></tr></thead>
            <tbody>
              {loading ? (<tr><td colSpan="7" className="table-cell text-center text-gray-500">Cargando...</td></tr>
              ) : licencias.length === 0 ? (<tr><td colSpan="7" className="table-cell text-center text-gray-500">No hay licencias registradas</td></tr>
              ) : (licencias.map(licencia => (
                <tr key={licencia.id} className="table-row">
                  <td className="table-cell font-medium text-[#0A0A0A]">{licencia.user_nombre}</td>
                  <td className="table-cell"><span className={`badge ${licencia.user_role === 'supervisor' ? 'badge-blue' : 'badge-orange'}`}>{licencia.user_role}</span></td>
                  <td className="table-cell"><span className={`inline-flex items-center gap-1 badge ${getTipoBadge(licencia.tipo)}`}>{getTipoIcon(licencia.tipo)}{getTipoLabel(licencia.tipo)}</span></td>
                  <td className="table-cell">{new Date(licencia.fecha_inicio + 'T12:00:00').toLocaleDateString('es-UY')}</td>
                  <td className="table-cell">{new Date(licencia.fecha_fin + 'T12:00:00').toLocaleDateString('es-UY')}</td>
                  <td className="table-cell text-gray-500 text-[12px]">{licencia.notas || '-'}</td>
                  <td className="table-cell"><button onClick={() => handleDelete(licencia.id)} className="btn-ghost p-1.5 text-[#EF4444] hover:text-[#EF4444]" data-testid={`delete-licencia-${licencia.id}`}><Trash2 size={14} /></button></td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="mac-popover max-w-md p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200"><DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">Nueva Licencia</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="p-5 space-y-4">
            <div><label className="form-label">Persona</label><select value={userId} onChange={(e) => setUserId(e.target.value)} className="form-input" data-testid="licencia-user"><option value="">Seleccionar persona</option>{users.map(u => (<option key={u.id} value={u.id}>{u.nombre} ({u.role})</option>))}</select></div>
            <div>
              <label className="form-label">Tipo de Licencia</label>
              <div className="segmented-control w-full">
                <button type="button" onClick={() => setTipo('anual')} className={`segmented-button flex-1 ${tipo === 'anual' ? 'active' : ''}`}><Palmtree size={14} className="inline mr-1" /> Anual</button>
                <button type="button" onClick={() => setTipo('enfermedad')} className={`segmented-button flex-1 ${tipo === 'enfermedad' ? 'active' : ''}`}><Stethoscope size={14} className="inline mr-1" /> Enfermedad</button>
                <button type="button" onClick={() => setTipo('otro')} className={`segmented-button flex-1 ${tipo === 'otro' ? 'active' : ''}`}><FileText size={14} className="inline mr-1" /> Otro</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Fecha Inicio</label><Popover><PopoverTrigger asChild><button type="button" className="form-input flex items-center justify-between text-left" data-testid="licencia-fecha-inicio">{fechaInicio ? format(fechaInicio, 'dd/MM/yyyy') : <span className="text-gray-400">Seleccionar</span>}<CalendarDays size={16} className="text-gray-400" /></button></PopoverTrigger><PopoverContent className="w-auto p-0 mac-popover" align="start"><Calendar mode="single" selected={fechaInicio} onSelect={setFechaInicio} locale={es} initialFocus className="rounded-md" /></PopoverContent></Popover></div>
              <div><label className="form-label">Fecha Fin</label><Popover><PopoverTrigger asChild><button type="button" className="form-input flex items-center justify-between text-left" data-testid="licencia-fecha-fin">{fechaFin ? format(fechaFin, 'dd/MM/yyyy') : <span className="text-gray-400">Seleccionar</span>}<CalendarDays size={16} className="text-gray-400" /></button></PopoverTrigger><PopoverContent className="w-auto p-0 mac-popover" align="start"><Calendar mode="single" selected={fechaFin} onSelect={setFechaFin} locale={es} initialFocus className="rounded-md" /></PopoverContent></Popover></div>
            </div>
            <div><label className="form-label">Notas (opcional)</label><textarea value={notas} onChange={(e) => setNotas(e.target.value)} className="form-input min-h-[80px] resize-none" placeholder="Observaciones..." data-testid="licencia-notas" /></div>
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setShowDialog(false)} className="btn-secondary">Cancelar</button><button type="submit" className="btn-primary" data-testid="save-licencia-btn">Registrar</button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
